import { Link } from 'react-router';
import { formatDate } from '@mys/contracts/format';
import type { PortalProject } from '@mys/contracts';
import { currentStep, STATUS_LABELS, useProjects } from '../lib/portal.js';

/* ============================================================================
   / — what is happening with my site.
   ----------------------------------------------------------------------------
   This is the screen the portal exists for. Someone opens it to answer one
   question — "is it ready yet?" — and the answer is at the top in a sentence,
   before the timeline that explains it.
   ========================================================================== */

export function HomePage() {
  const projects = useProjects();

  if (projects.isPending) return <p className="muted">Load ho raha hai…</p>;

  if (projects.error) {
    return (
      <p className="alert" role="alert">
        Abhi load nahi ho paya. Thodi der baad dobara kholiye.
      </p>
    );
  }

  const items = projects.data?.projects ?? [];

  if (items.length === 0) {
    return (
      <section className="card">
        <h1 className="h1">Abhi koi project nahi</h1>
        <p className="muted">
          Aapke naam par abhi koi kaam chalu nahi hai. Kuch shuru karna hai to WhatsApp par bata
          dijiye.
        </p>
        <a className="btn" href="https://wa.me/918630415226">
          WhatsApp kijiye
        </a>
      </section>
    );
  }

  return (
    <>
      <h1 className="h1">Aapka project</h1>
      {items.map((project) => (
        <ProjectCard key={project.id} project={project} />
      ))}
    </>
  );
}

function ProjectCard({ project }: { project: PortalProject }) {
  const step = currentStep(project.timeline);

  return (
    <section className="card stack">
      <div>
        <p className="kicker">{project.plan} plan</p>
        <h2 className="h2">{STATUS_LABELS[project.status] ?? project.status}</h2>
        <p className="muted">{step?.label}</p>
      </div>

      {/* The promise, and whether we are keeping it. `overdue` is computed by
          the API — never by comparing dates on the phone, whose clock is not
          what a refund is measured against. */}
      {project.promisedDate ? (
        <p
          className={project.overdue ? 'alert' : 'muted'}
          role={project.overdue ? 'alert' : undefined}
        >
          {project.overdue
            ? `Humne ${formatDate(project.promisedDate)} kaha tha aur late ho gaye. Maafi chahte hain — /refund ke hisaab se aapko 10% aur ek free care month milta hai.`
            : `Target: ${formatDate(project.promisedDate)}`}
        </p>
      ) : null}

      <ol className="timeline">
        {project.timeline.map((item) => (
          <li
            key={item.status}
            className={
              item.current
                ? 'timeline__step timeline__step--now'
                : item.done
                  ? 'timeline__step timeline__step--done'
                  : 'timeline__step'
            }
          >
            <span className="timeline__dot" aria-hidden="true" />
            <span className="timeline__label">{item.label}</span>
            {item.at ? <span className="timeline__at">{formatDate(item.at)}</span> : null}
          </li>
        ))}
      </ol>

      <div className="row">
        {project.previewUrl ? (
          <a className="btn" href={project.previewUrl} target="_blank" rel="noreferrer">
            Preview dekhiye
          </a>
        ) : null}
        {project.liveUrl ? (
          <a className="btn btn--ghost" href={project.liveUrl} target="_blank" rel="noreferrer">
            Live site
          </a>
        ) : null}
        <Link className="btn btn--ghost" to="/edit-requests/new">
          Kuch badalna hai
        </Link>
      </div>

      {project.domain ? <p className="muted small">Domain: {project.domain}</p> : null}
    </section>
  );
}
